import Events from '../utils/events';
import Buffer from '../utils/buffer';
import { EV_COOKIE_CONSENT_UPDATED } from '../components-legacy/CookieConsent';
import { FlagType } from '../../@types/product.enum';

import {
  EV_ADD_TO_CART,
  EV_BEGIN_PAYMENT_FAILED,
  EV_BEGIN_PAYMENT,
  EV_BLOCK_CLICKED,
  EV_BLOCK_IMPRESSION,
  EV_CART_VIEW,
  EV_CHECKOUT_OPTION,
  EV_CHECKOUT_VIEW,
  EV_GENERIC,
  EV_PAGE_VIEW,
  EV_PAYMENT_ABORTED,
  EV_PAYMENT_BROWSER_BACK,
  EV_PRODUCT_CLICK,
  EV_PRODUCT_IMPRESSION,
  EV_PRODUCT_VIEW,
  EV_PURCHASE,
  EV_REMOVE_FROM_CART,
  EV_BODY_SCROLL,
  EV_SEARCH,
  EV_SEARCH_NO_HITS,
  EV_SEARCH_NO_SUGGESTIONS,
  EV_SEARCH_ON_SUGGESTION_CLICK,
  EV_ALERTBANNER_CLICKED,
} from './events';

declare const dataLayer: any[];

interface IEvent extends Record<string, any> {
  event?: string;
}

let buffer: Buffer<IProductImpression> | undefined = undefined;
let productImpressionBuffer: Buffer<IGA4Product> | undefined = undefined;

const trackEvent = (event: IEvent) => {
  if (window.dataLayer) {
    dataLayer.push({ ecommerce: null });
    dataLayer.push(event);
  }
};

const getProductName = (product: ITrackableProduct) => {
  if (!product.description) {
    return product.name;
  }

  if (product.description.constructor === String) {
    return `${product.name} ${product.description}`;
  }

  const desc = product.description as IProductDescription;

  if (desc?.short) {
    return `${product.name} ${desc?.short}`;
  }
};

const getTrackableProduct = (product: ITrackableProduct, variant?: string, quantity = 1) =>
  ({
    brand: product.brand?.name || 'MIO',
    category: product.productType,
    id: product.moderArtikelId || product.m3Id,
    name: getProductName(product),
    price: product.price?.price,
    quantity,
    variant:
      variant ||
      product.configurations?.['sofaUpholster']?.name ||
      product.specifications?.colorDescription ||
      product.specifications?.colorRange,
  }) as IUniversalGAProduct;

const getDCOFlagInformation = (flag: IFlag | undefined, discount: number) =>
  ({
    id: flag?.id,
    type: flag?.type,
    offerLimit: flag?.offerLimit?.value,
    priceCustomization: {
      textColor: flag?.priceCustomization?.textColor,
      backgroundColor: flag?.priceCustomization?.backgroundColor,
    },
    productBanner: {
      backgroundColor: flag?.productBanner?.design.backgroundColor,
      textColor: flag?.productBanner?.design.textColor,
      text:
        flag?.productBanner?.showDynamicPercentage && discount
          ? flag?.productBanner?.text + ` -${discount}%`
          : flag?.productBanner?.text,
    },
  }) as IDcoTrackingFlag;

const getDCOTrackableProduct = (product: any) => {
  const flag = product?.flags.find(
    (flag: IFlag) => flag.type == FlagType.main || flag.type == FlagType.outlet,
  );
  return {
    item_id: product.m3Id ?? product.id,
    item_name: getProductName(product),
    item_brand: product.brand?.name || 'MIO',
    currency: 'SEK',
    discount: product.price?.before ? product.price.before - product.price.price : 0,
    flag: getDCOFlagInformation(flag, product.price?.discountPercentage),
    item_category: product.productType ?? product.categorization.categoryLeafName,
    item_variant:
      product.configurations?.['sofaUpholster']?.name ||
      product.specifications?.colorDescription ||
      product.specifications?.colorRange,
    specifications: product.specifications,
    price: product.price,
  } as IDCOTrackingProduct;
};

const getTrackableProductGA4 = (product: any, variant?: string, quantity = 1) =>
  ({
    item_id: product.moderArtikelId || product.m3Id,
    item_name: getProductName(product),
    currency: 'SEK',
    discount: product.price?.before ? product.price.before - product.price.price : 0,
    item_brand: product.brand?.name || 'MIO',
    item_category: product.productType,
    item_variant:
      variant ||
      product.configurations?.['sofaUpholster']?.name ||
      product.specifications?.colorDescription ||
      product.specifications?.colorRange,
    price: product.price?.price,
    quantity,
  }) as IGA4Product;

export default () => {
  buffer = new Buffer<IProductImpression>({ countThreshold: 8, timeThreshold: 2000 });
  productImpressionBuffer = new Buffer<IGA4Product>({
    countThreshold: 8,
    timeThreshold: 2000,
  });

  Events.on(EV_COOKIE_CONSENT_UPDATED, () => {
    setTimeout(() => {
      trackEvent({ event: 'consentUpdated' });
    }, 500);
  });

  Events.on(EV_ADD_TO_CART, (product: ITrackableProduct, variant?: string, quantity = 1) => {
    try {
      trackEvent({
        event: 'addToCart',
        ecommerce: {
          add: {
            products: [getTrackableProduct(product, variant, quantity)],
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  /*
   * Event for GA4 tracking
   */
  Events.on(EV_ADD_TO_CART, (product: ITrackableProduct, variant?: string, quantity = 1) => {
    try {
      trackEvent({
        event: 'add_to_cart',
        ecommerce: {
          items: [getTrackableProductGA4(product, variant, quantity)],
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_REMOVE_FROM_CART, (product: ITrackableProduct, variant?: string, quantity = 1) => {
    try {
      trackEvent({
        event: 'removeFromCart',
        ecommerce: {
          remove: {
            products: [getTrackableProduct(product, variant, quantity)],
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  /*
   * Event for GA4 tracking
   */
  Events.on(EV_REMOVE_FROM_CART, (product: ITrackableProduct, variant?: string, quantity = 1) => {
    try {
      trackEvent({
        event: 'remove_from_cart',
        ecommerce: {
          items: [getTrackableProductGA4(product, variant, quantity)],
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_PRODUCT_VIEW, (product: ITrackableProduct) => {
    try {
      trackEvent({
        event: 'productView',
        ecommerce: {
          detail: {
            products: [getTrackableProduct(product)],
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  /*
   Event for DCO productInformation. 
    Used by Wavemaker to get relevant productInformation
   */
  Events.on(EV_PRODUCT_VIEW, (product: any) => {
    try {
      trackEvent({
        event: 'dcoProductInformation',
        product: [getDCOTrackableProduct(product)],
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  /*
   * Event for GA4 tracking
   */
  Events.on(EV_PRODUCT_VIEW, (product: ITrackableProduct) => {
    try {
      trackEvent({
        event: 'view_item',
        ecommerce: {
          items: [getTrackableProductGA4(product)],
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_CART_VIEW, ({ products }: { products: ITrackableProduct[] }) => {
    try {
      trackEvent({
        event: 'cartView',
        ecommerce: {
          cart: {
            products: products.map((p) => getTrackableProduct(p, undefined, p.quantity)),
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(
    EV_CART_VIEW,
    ({ products, value }: { products: ITrackableProduct[]; value?: number }) => {
      try {
        trackEvent({
          event: 'view_cart',
          ecommerce: {
            items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
            value,
            currency: 'SEK',
          },
        });
      } catch (e) {
        console.warn('Tracking failed', e);
      }
    },
  );

  Events.on(
    EV_CHECKOUT_VIEW,
    ({
      products,
      step,
      option,
    }: {
      products: ITrackableProduct[];
      step: number;
      option?: string;
    }) => {
      try {
        trackEvent({
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: { action: 'checkout', option, step },
              products: products.map((p) => getTrackableProduct(p)),
            },
            currencyCode: 'SEK',
          },
        });
      } catch (e) {
        console.warn('Tracking failed', e);
      }
    },
  );

  /*
   * Event for GA4 tracking
   */

  interface ICheckoutView {
    products: ITrackableProduct[];
    step: number;
    option?: string;
    deliveryValue?: number;
  }

  Events.on(EV_CHECKOUT_VIEW, ({ products, step, option, deliveryValue }: ICheckoutView) => {
    if (step === 1) {
      try {
        trackEvent({
          event: 'begin_checkout',
          ecommerce: {
            items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
          },
        });
        if (option) {
          trackEvent({
            event: 'add_shipping_info',
            ecommerce: {
              items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
              value: deliveryValue,
              currency: 'SEK',
              // coupon: '',
              shipping_tier: option,
            },
          });
        }
      } catch (e) {
        console.warn('Tracking failed', e);
      }
    }

    try {
      const checkoutSteps = ['', 'Delivery', 'Customer information', 'Payment'];

      trackEvent({
        event: 'checkout_step',
        ecommerce: {
          items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
          step: checkoutSteps[step],
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_CHECKOUT_OPTION, ({ step, option }: { step: number; option?: string }) => {
    try {
      trackEvent({
        event: 'checkoutOption',
        ecommerce: {
          checkout: {
            actionField: { action: 'checkout_option', option, step },
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(
    EV_CHECKOUT_OPTION,
    ({
      products,
      step,
      option,
      deliveryValue,
    }: {
      products: any[];
      step: number;
      option?: string;
      deliveryValue?: number;
    }) => {
      if (step === 1) {
        try {
          trackEvent({
            event: 'add_shipping_info',
            ecommerce: {
              items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
              value: deliveryValue,
              currency: 'SEK',
              // coupon: '',
              shipping_tier: option,
            },
          });
        } catch (e) {
          console.warn('Tracking failed', e);
        }
      } else if (step === 3) {
        try {
          trackEvent({
            event: 'add_payment_info',
            ecommerce: {
              items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
              value: '',
              currency: 'SEK',
              // coupon: '',
              payment_type: option,
            },
          });
        } catch (e) {
          console.warn('Tracking failed', e);
        }
      }
    },
  );

  Events.on(EV_GENERIC, ({ event, ...props }: { event: string }) => {
    try {
      trackEvent({
        event,
        ...props,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_BEGIN_PAYMENT, (step: number, option?: string) => {
    try {
      trackEvent({
        event: 'checkoutOption',
        ecommerce: {
          checkout: {
            actionField: { action: 'checkout_option', option, step },
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  /*
   * Event for GA4 tracking
   */
  Events.on(EV_BEGIN_PAYMENT, (_: number, option?: string, products?: ITrackableProduct[]) => {
    try {
      trackEvent({
        event: 'begin_payment',
        option,
        ecommerce: products
          ? {
              items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
            }
          : null,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_PAYMENT_ABORTED, (status: string) => {
    try {
      trackEvent({
        event: 'paymentAborted',
        label: status,
      });
    } catch (e) {
      console.warn('Tracking payment aborted failed', e);
    }
  });

  Events.on(EV_BEGIN_PAYMENT_FAILED, (error: string) => {
    try {
      trackEvent({
        event: 'paymentAborted',
        label: error,
      });
    } catch (e) {
      console.warn('Tracking begin payment failed', e);
    }
  });

  Events.on(EV_PAYMENT_BROWSER_BACK, () => {
    try {
      trackEvent({
        event: 'paymentAborted',
        label: 'browserBack',
      });
    } catch (e) {
      console.warn('Tracking payment browser back failed', e);
    }
  });

  Events.on(
    EV_PURCHASE,
    (
      products: ITrackableProduct[],
      order: Commerce.IOrderResponse,
      selectedDeliveryMethods: string,
      member: boolean,
    ) => {
      try {
        trackEvent({
          event: 'purchase',
          ecommerce: {
            purchase: {
              actionField: {
                action: 'purchase',
                id: order.id,
                revenue: order.cart.total,
                tax: order.cart.vat,
                shipping: order.cart.deliveryPrice,
              },
              products: products.map((p) => getTrackableProduct(p)),
            },
            currencyCode: 'SEK',

            // GA4 data below
            transaction_id: order.id,
            value: order.cart.total,
            tax: order.cart.vat,
            shipping: order.cart.deliveryPrice,
            currency: 'SEK',
            items: products.map((p) => getTrackableProductGA4(p, undefined, p.quantity)),
            coupon: order.cart.coupon?.value,
          },
          deliveryType: selectedDeliveryMethods,
          member,
        });
        trackEvent({ ecommerce: null });
      } catch (e) {
        console.warn('Tracking failed', e);
      }
    },
  );

  Events.on(EV_PAGE_VIEW, () => {
    try {
      setTimeout(() => {
        trackEvent({
          event: 'asyncPageView',
        });
      }, 0);
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_SEARCH_NO_HITS, (searchTerm: string) => {
    try {
      trackEvent({
        event: 'searchNoHits',
        searchTerm,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_SEARCH_NO_SUGGESTIONS, (searchTerm: string) => {
    try {
      trackEvent({
        event: 'searchNoSuggestions',
        searchTerm,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_SEARCH_ON_SUGGESTION_CLICK, (searchTerm: string, suggestion: string) => {
    try {
      trackEvent({
        event: 'searchSuggestionClicked',
        searchTerm,
        suggestion,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_PRODUCT_IMPRESSION, (list: string, position: number, product: ITrackableProduct) => {
    try {
      const trackableProduct = {
        ...getTrackableProduct(product),
        list,
        position,
      };

      buffer?.push(trackableProduct);

      /*
       * Event for GA4 tracking
       */
      const trackableProductGA4 = {
        ...getTrackableProductGA4(product),
        item_list_name: list,
        index: position - 1,
      };
      productImpressionBuffer?.push(trackableProductGA4);
    } catch (e) {
      console.warn('Failed to add impression to buffer', e);
    }
  });

  buffer?.listen((impressions) => {
    try {
      trackEvent({
        event: 'productImpression',
        ecommerce: {
          impressions,
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Failed to track product impressions to datalayer', e);
    }
  });

  productImpressionBuffer?.listen((impressions) => {
    try {
      trackEvent({
        event: 'view_item_list',
        ecommerce: {
          items: impressions,
        },
      });
    } catch (e) {
      console.warn('Failed to track product impressions to datalayer', e);
    }
  });

  Events.on(EV_PRODUCT_CLICK, (list: string, position: number, product: ITrackableProduct) => {
    try {
      const trackableProduct = { ...getTrackableProduct(product), position };

      trackEvent({
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: { list },
            products: [trackableProduct],
          },
          currencyCode: 'SEK',
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }

    /*
     * Event for GA4 tracking
     */
    try {
      const trackableProductGA4 = {
        ...getTrackableProductGA4(product),
        item_list_name: list,
        index: position - 1,
      };
      trackEvent({
        event: 'select_item',
        ecommerce: {
          items: [trackableProductGA4],
        },
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_SEARCH, (query: string) => {
    try {
      trackEvent({
        event: 'search',
        searchTerm: query,
      });
    } catch (e) {
      console.warn('Failed to track search event', e);
    }
  });

  Events.on(EV_BLOCK_IMPRESSION, (blockType: string, order: number) => {
    try {
      trackEvent({
        event: 'blockImpression',
        blockType,
        order,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_BLOCK_CLICKED, (blockType: string, order: number, href: string) => {
    try {
      trackEvent({
        event: 'blockClicked',
        blockType,
        order,
        href,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_ALERTBANNER_CLICKED, (alertBannerName: string) => {
    try {
      trackEvent({
        event: 'alertBannerClicked',
        name: alertBannerName,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });

  Events.on(EV_BODY_SCROLL, (scrollDepth: number) => {
    try {
      trackEvent({
        event: 'bodyScroll',
        scrollDepth,
      });
    } catch (e) {
      console.warn('Tracking failed', e);
    }
  });
};
